/**
 * ticketService.ts
 * 
 * Servicio unificado de tickets: generación de payload, resolución de config,
 * renderizado HTML para impresión y preview, generación de barcode.
 * 
 * NO modifica lógica de venta. Solo opera POST-VENTA.
 */

import { fetchAPI } from './apiClient';

// ─── Tipos ───────────────────────────────────────────────

export type TicketAncho = 80 | 58;
export type TicketFontWeight = 'normal' | 'bold' | 'extra-bold';
export type TicketFontSize = 'normal' | 'grande' | 'extra-grande' | 'gigante' | 'extra-gigante';

export interface TicketConfig {
  nombre: string;
  notas: string;
  ancho: TicketAncho;
  logoUrl: string | null;
  logoDataUrl: string | null;
  fontWeight: TicketFontWeight;
  fontSize: TicketFontSize;
}

/** Ajustes visuales almacenados en localStorage */
export interface TicketVisualConfig {
  logoDataUrl?: string | null;
  fontWeight?: TicketFontWeight;
  fontSize?: TicketFontSize;
}

// localStorage keys
const LS_VISUAL_VENDEDOR = 'tiempos_ticket_visual_vendedor';
const LS_VISUAL_LOCAL = 'tiempos_ticket_visual_local';

export function guardarVisualConfig(role: 'vendedor' | 'local', cfg: TicketVisualConfig): void {
  const key = role === 'vendedor' ? LS_VISUAL_VENDEDOR : LS_VISUAL_LOCAL;
  localStorage.setItem(key, JSON.stringify(cfg));
  invalidarConfigCache();
}

export function leerVisualConfig(role: 'vendedor' | 'local'): TicketVisualConfig {
  const key = role === 'vendedor' ? LS_VISUAL_VENDEDOR : LS_VISUAL_LOCAL;
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export interface TicketJugada {
  num1: string;
  num2?: string | null;
  monto: number;
}

export interface TicketPayload {
  ticketCode: string;
  ticketId: string;
  sorteoNombre: string;
  sorteoCierre: string;
  sorteoFecha: string;
  fecha: string;
  vendedorNombre: string;
  localNombre: string;
  metodoPago: string;
  referencia: string | null;
  total: number;
  jugadas: TicketJugada[];
  config: TicketConfig;
  esReimpresion: boolean;
  pagaVecesAplicado: string | null;
  clienteNombre: string;
}

// ─── Config cache ────────────────────────────────────────

let _configCache: TicketConfig | null = null;
let _configCacheTime = 0;
const CONFIG_TTL = 30_000; // 30s

/**
 * Resuelve configuración en cadena: VENDEDOR → LOCAL → DEFAULT
 * - nombre / notas: desde API (vendedor override → local default)
 * - logo / font: desde localStorage (vendedor override → local default)
 */
export async function resolverTicketConfig(): Promise<TicketConfig> {
  const now = Date.now();
  if (_configCache && now - _configCacheTime < CONFIG_TTL) {
    return _configCache;
  }

  const cfg: TicketConfig = {
    nombre: 'TiemPos',
    notas: '',
    ancho: 80,
    logoUrl: null,
    logoDataUrl: null,
    fontWeight: 'normal',
    fontSize: 'normal',
  };

  // ── Visual config desde localStorage (vendedor → local) ──
  const visualVendedor = leerVisualConfig('vendedor');
  const visualLocal = leerVisualConfig('local');

  cfg.logoDataUrl = visualVendedor.logoDataUrl || visualLocal.logoDataUrl || null;
  cfg.fontWeight = visualVendedor.fontWeight || visualLocal.fontWeight || 'normal';
  cfg.fontSize = visualVendedor.fontSize || visualLocal.fontSize || 'normal';

  // ── 1. Intentar config de vendedor (API) ──
  try {
    const vendedor = await fetchAPI<{
      ticket_nombre: string | null;
      ticket_notas: string | null;
    }>('/operaciones/vendedor/ticket-config');

    if (vendedor.ticket_nombre) cfg.nombre = vendedor.ticket_nombre;
    if (vendedor.ticket_notas) cfg.notas = vendedor.ticket_notas;

    if (vendedor.ticket_nombre) {
      _configCache = { ...cfg };
      _configCacheTime = now;
      return _configCache;
    }
  } catch {
    // vendedor config no disponible, seguir
  }

  // ── 2. Intentar config del local (API) ──
  try {
    const local = await fetchAPI<{
      nombre_ticket: string | null;
      notas_ticket: string | null;
    }>('/operaciones/local/ticket-config');

    if (local.nombre_ticket && cfg.nombre === 'TiemPos') cfg.nombre = local.nombre_ticket;
    if (local.notas_ticket && !cfg.notas) cfg.notas = local.notas_ticket;
  } catch {
    // local config no disponible, usar defaults
  }

  _configCache = { ...cfg };
  _configCacheTime = now;
  return _configCache;
}

/**
 * Invalida la caché de configuración
 */
export function invalidarConfigCache(): void {
  _configCache = null;
  _configCacheTime = 0;
}

// ─── Payload builder ─────────────────────────────────────

export interface CrearPayloadOpts {
  ticketCode: string;
  ticketId: string;
  sorteoNombre: string;
  sorteoCierre: string;
  sorteoFecha?: string;
  metodoPago: string;
  referencia?: string | null;
  total: number;
  jugadas: TicketJugada[];
  vendedorNombre?: string;
  localNombre?: string;
  esReimpresion?: boolean;
  pagaVecesAplicado?: string | null;
  clienteNombre?: string;
}

export async function crearTicketPayload(opts: CrearPayloadOpts): Promise<TicketPayload> {
  const config = await resolverTicketConfig();

  return {
    ticketCode: opts.ticketCode,
    ticketId: opts.ticketId,
    sorteoNombre: opts.sorteoNombre,
    sorteoCierre: opts.sorteoCierre,
    sorteoFecha: opts.sorteoFecha || '',
    fecha: new Date().toLocaleString('es-CR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    }),
    vendedorNombre: opts.vendedorNombre || '',
    localNombre: opts.localNombre || '',
    metodoPago: opts.metodoPago,
    referencia: opts.referencia || null,
    total: opts.total,
    jugadas: opts.jugadas,
    config,
    esReimpresion: opts.esReimpresion || false,
    pagaVecesAplicado: opts.pagaVecesAplicado ?? null,
    clienteNombre: opts.clienteNombre || 'Cliente',
  };
}

// ─── HTML Render ─────────────────────────────────────────

/** Mapea TicketFontWeight → CSS font-weight */
function cssWeight(w: TicketFontWeight): string {
  return w === 'extra-bold' ? '900' : w === 'bold' ? '700' : '400';
}

function cssStrongWeight(base: string): string {
  const n = parseInt(base, 10);
  return String(Math.min(900, n + 200));
}

function cssSectionWeight(base: string): string {
  const n = parseInt(base, 10);
  return String(Math.min(900, n + 300));
}

/** Mapea TicketFontSize → tamaños px */
function cssSizes(sz: TicketFontSize, anchoMM: TicketAncho) {
  const map: Record<TicketAncho, Record<TicketFontSize, { body: number; monto: number; nums: number; total: number; nombre: number; seccion: number }>> = {
    58: {
      'normal':       { body: 11, monto: 14, nums: 13, total: 15, nombre: 13, seccion: 12 },
      'grande':       { body: 12, monto: 15, nums: 14, total: 16, nombre: 14, seccion: 13 },
      'extra-grande': { body: 14, monto: 17, nums: 16, total: 18, nombre: 16, seccion: 15 },
      'gigante':      { body: 16, monto: 21, nums: 19, total: 23, nombre: 18, seccion: 16 },
      'extra-gigante':{ body: 18, monto: 24, nums: 22, total: 26, nombre: 20, seccion: 18 },
    },
    80: {
      'normal':       { body: 13, monto: 16, nums: 15, total: 18, nombre: 15, seccion: 14 },
      'grande':       { body: 14, monto: 17, nums: 16, total: 19, nombre: 16, seccion: 15 },
      'extra-grande': { body: 16, monto: 19, nums: 18, total: 21, nombre: 18, seccion: 17 },
      'gigante':      { body: 18, monto: 24, nums: 22, total: 26, nombre: 21, seccion: 19 },
      'extra-gigante':{ body: 20, monto: 28, nums: 26, total: 30, nombre: 24, seccion: 21 },
    },
  };
  return map[anchoMM][sz];
}

/**
 * Genera HTML completo del ticket térmico.
 *
 * Diseño limpio de banca:
 *   [LOGO]
 *   NOMBRE (bold, centrado)
 *   fecha hora (centrado)
 *   ─────────────────
 *   SORTEO · CIERRE
 *   ─────────────────
 *   ₡1,000
 *   22 23 24 25 26
 *
 *   ₡500
 *   01 15
 *
 *   REVENTADO
 *   ₡1,000
 *   22(R1)  23(R2)
 *   ─────────────────
 *   TOTAL ₡2,500
 *
 *   TICKET: #A8F2Q
 *   [barcode]
 *   notas
 *
 * Reglas:
 *  - Todo centrado
 *  - Montos en tamaño grande, solos en su línea
 *  - Números debajo separados por espacio
 *  - Máximo 2 separadores visuales
 *  - Sin tablas, sin columnas
 *  - Preview = Impresión (mismo HTML)
 */
export function renderTicketHTML(payload: TicketPayload): string {
  const { config, jugadas, esReimpresion } = payload;
  const anchoMM = config.ancho;
  const anchoPx = anchoMM === 58 ? 384 : 512;
  const charWidth = anchoMM === 58 ? 32 : 42;
  const sz = cssSizes(config.fontSize, anchoMM);
  const weight = cssWeight(config.fontWeight);
  const weightStrong = cssStrongWeight(weight);
  const weightSection = cssSectionWeight(weight);
  const extraBoldEnhance = config.fontWeight === 'extra-bold'
    ? 'text-shadow: 0.25px 0 currentColor, -0.25px 0 currentColor;'
    : '';

  // charWidth para las jugadas: basado en el font-size de monto (más grande que body)
  // Courier New monospace: ~0.6em por carácter
  const montoCharPx = sz.monto * 0.6;
  const charWidthMonto = Math.floor((anchoPx - 8) / montoCharPx); // 8px = padding total

  // ── Clasificar jugadas por tipo (usando num2 existente) ──
  type SeccionTipo = 'NORMAL' | 'REVENTADO' | 'MEGA' | 'MONAZOS' | 'FECHAS' | 'PARLEY';

  function clasificarJugada(j: TicketJugada): SeccionTipo {
    // REV1 = apuesta base → va en NORMAL
    if (j.num2 && /^REV1$/i.test(j.num2)) return 'NORMAL';
    // REV2 = apuesta reventado → va en REVENTADO
    if (j.num2 && /^REV2$/i.test(j.num2)) return 'REVENTADO';
    if (j.num2 && /^MEGA:/i.test(j.num2)) return 'MEGA';
    if (j.num2 && /^(EN_ORDEN|DESORDEN|PRIMEROS_2|ULTIMOS_2|ORDEN|P2|U2)$/i.test(j.num2)) return 'MONAZOS';
    if (/^\d{2}-\d{2}$/.test(j.num1)) return 'FECHAS';
    if (j.num2 && /^\d{2}$/.test(j.num2)) return 'PARLEY';
    return 'NORMAL';
  }

  // Agrupar en secciones preservando orden de aparición
  const secciones = new Map<SeccionTipo, TicketJugada[]>();
  for (const j of jugadas) {
    const tipo = clasificarJugada(j);
    const arr = secciones.get(tipo) || [];
    arr.push(j);
    secciones.set(tipo, arr);
  }

  // Label map para MONAZOS subtipos
  const monazoLabel: Record<string, string> = {
    ORDEN: 'En Orden', EN_ORDEN: 'En Orden',
    DESORDEN: 'Desorden',
    P2: 'Primeros 2', PRIMEROS_2: 'Primeros 2',
    U2: 'Últimos 2', ULTIMOS_2: 'Últimos 2',
  };

  // Renderizar bloques de jugadas con encabezados
  let jugadasHTML = '';
  for (const [tipo, jugs] of secciones) {
    if (tipo === 'MONAZOS') {
      // Agrupar por subtipo (num2) y mostrar cada uno como sub-sección
      jugadasHTML += `<div class="section-label">MONAZOS</div>\n`;
      const subMap = new Map<string, TicketJugada[]>();
      for (const j of jugs) {
        const key = (j.num2 || 'ORDEN').toUpperCase();
        const arr = subMap.get(key) || [];
        arr.push(j);
        subMap.set(key, arr);
      }
      for (const [subTipo, subJugs] of subMap) {
        const label = monazoLabel[subTipo] || subTipo;
        jugadasHTML += `<div class="sorteo-info" style="font-weight:bold;margin-top:4px;">${escHtml(label)}</div>\n`;
        jugadasHTML += buildJugadasBlock(subJugs, charWidthMonto, 'MONAZOS', esReimpresion) + '\n';
      }
    } else {
      jugadasHTML += `<div class="section-label">${escHtml(tipo)}</div>\n`;
      jugadasHTML += buildJugadasBlock(jugs, charWidthMonto, tipo, esReimpresion) + '\n';
    }
  }

  // ── Logo ──
  const logoSrc = config.logoDataUrl || config.logoUrl;
  const logoBlock = logoSrc
    ? `<div class="logo"><img src="${logoSrc}" alt="" style="max-width:${Math.round(anchoPx * 0.45)}px;max-height:56px;" /></div>`
    : '';

  // ── Reimpresión ──
  const reimpBlock = esReimpresion
    ? `<div class="reimp">******** REIMPRESIÓN ********</div>`
    : '';

  // ── Barcode (solo original) ──
  const barcodeBlock = !esReimpresion
    ? `<div class="barcode-wrap"><svg id="ticket-barcode"></svg></div>`
    : '';

  // ── Notas (no se muestran en reimpresión) ──
  const notasBlock = (!esReimpresion && config.notas)
    ? `<div class="notas">${wrapText(config.notas, charWidth)}</div>`
    : '';

  // ── Disclaimer reimpresión ──
  const reimpDisclaimer = esReimpresion
    ? `<div class="reimp-disclaimer">Este ticket es una reimpresión.\nNo sustituye el original para\nefectos de validación automática.</div>`
    : '';

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8"/>
<title>Ticket ${payload.ticketCode}</title>
<style>
@page { size: ${anchoMM}mm auto; margin: 0; }
* { margin:0; padding:0; box-sizing:border-box; }
body {
  width: ${anchoPx}px;
  max-width: ${anchoPx}px;
  overflow-x: hidden;
  font-family: 'Courier New', Courier, monospace;
  font-size: ${sz.body}px;
  font-weight: ${weight};
  line-height: 1.35;
  color: #000;
  background: #fff;
  padding: 6px 4px 10px;
  text-align: center;
}
.logo { margin-bottom: 4px; }
.logo img { display: inline-block; }
.reimp {
  padding: 3px 0;
  border: 2px dashed #dc2626;
  color: #dc2626;
  font-weight: ${weightSection};
  letter-spacing: 2px;
  font-size: ${sz.body}px;
  margin-bottom: 4px;
  ${extraBoldEnhance}
}
.nombre {
  font-size: ${sz.nombre}px;
  font-weight: ${weightSection};
  letter-spacing: 0.5px;
  ${extraBoldEnhance}
}
.fecha {
  font-size: ${sz.body - 1}px;
  color: #333;
  margin-bottom: 2px;
}
.sep {
  color: #aaa;
  font-size: ${sz.body - 2}px;
  margin: 3px 0;
  letter-spacing: -0.5px;
  overflow: hidden;
  white-space: nowrap;
}
.sorteo-info {
  font-size: ${sz.body}px;
  margin: 1px 0;
}
.sorteo-nombre {
  font-size: ${sz.nombre + 2}px;
  font-weight: ${weightSection};
  margin: 2px 0;
  letter-spacing: 0.3px;
  ${extraBoldEnhance}
}
.jugada-row {
  font-size: ${sz.monto}px;
  font-weight: ${weightStrong};
  text-align: left;
  margin: 2px auto;
  line-height: 1.4;
  white-space: pre;
  display: block;
  width: max-content;
  ${extraBoldEnhance}
}
.section-label {
  font-size: ${sz.seccion}px;
  font-weight: ${weightSection};
  margin-top: 10px;
  margin-bottom: 1px;
  letter-spacing: 1.5px;
  text-decoration: underline;
  text-underline-offset: 3px;
  text-align: center;
  ${extraBoldEnhance}
}
.total {
  font-size: ${sz.total}px;
  font-weight: ${weightSection};
  margin: 4px 0 2px;
  ${extraBoldEnhance}
}
.ticket-code {
  font-size: ${sz.body}px;
  font-weight: ${weightStrong};
  margin: 2px 0;
  ${extraBoldEnhance}
}
.barcode-wrap {
  margin: 6px 0 2px;
}
.notas {
  font-size: ${sz.body - 1}px;
  color: #444;
  margin-top: 4px;
  white-space: pre-wrap;
  word-break: break-all;
}
.reimp-disclaimer {
  font-size: ${sz.body - 2}px;
  color: #666;
  margin-top: 6px;
  padding: 4px 2px;
  border-top: 1px dashed #999;
  white-space: pre-wrap;
  line-height: 1.3;
}
@media print { body { padding: 0 4px 6px; } }
</style>
</head>
<body>
${reimpBlock}
${logoBlock}
<div class="nombre">${escHtml(config.nombre)}</div>
${payload.vendedorNombre ? `<div class="sorteo-info">Vendedor: ${escHtml(payload.vendedorNombre)}</div>` : ''}
<div class="sorteo-info">Cliente: ${escHtml(payload.clienteNombre)}</div>
<div class="fecha">${escHtml(payload.fecha)}</div>
<div class="sep">${'*'.repeat(charWidth)}</div>
<div class="sorteo-nombre">${escHtml(payload.sorteoNombre)}</div>
${payload.sorteoFecha ? `<div class="sorteo-info">Fecha: ${escHtml(payload.sorteoFecha)}</div>` : ''}
${payload.pagaVecesAplicado ? `<div class="sorteo-info">Paga: ${escHtml(payload.pagaVecesAplicado)}</div>` : ''}
<div class="sep">${'*'.repeat(charWidth)}</div>
${jugadasHTML}
<div class="sep">${'*'.repeat(charWidth)}</div>
<div class="total">TOTAL ₡${payload.total.toLocaleString()}</div>
<div class="ticket-code">TICKET: ${escHtml(payload.ticketCode)}</div>
${barcodeBlock}
${notasBlock}
${reimpDisclaimer}
${!esReimpresion ? `
<script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js"><\/script>
<script>
try { if(typeof JsBarcode!=='undefined') JsBarcode("#ticket-barcode","${payload.ticketCode}",{format:"CODE128",width:2,height:40,displayValue:false,margin:4}); } catch(e){}
<\/script>` : ''}
</body>
</html>`;
}

/**
 * Agrupa jugadas por monto → bloques limpios.
 *
 * Formato inline (monto y números en misma línea):
 *   ₡1,000   22 23 24 25 26 27 28
 *
 * Si los números no caben, continúan en la siguiente línea
 * alineados bajo los números (no bajo el monto).
 *
 * Formatea según tipo de sección:
 *  - REVENTADO: 22(R1) 23(R2)
 *  - MEGA: extrae número mega de num2
 *  - PARLEY: num1+num2
 *  - MONAZOS: número con tipo
 *  - NORMAL/FECHAS: num1 directo
 */
function buildJugadasBlock(
  jugadas: TicketJugada[],
  charWidth: number,
  tipo: string,
  esReimpresion: boolean = false,
): string {
  if (jugadas.length === 0) return '';

  // Agrupar por monto preservando orden de aparición
  const montoMap = new Map<number, string[]>();
  for (const j of jugadas) {
    const arr = montoMap.get(j.monto) || [];
    if (tipo === 'REVENTADO' && j.num2) {
      // Número limpio sin tag
      arr.push(j.num1);
    } else if (tipo === 'MEGA' && j.num2) {
      const megaNum = j.num2.replace(/^MEGA:/i, '');
      arr.push(megaNum);
    } else if (tipo === 'PARLEY' && j.num2) {
      arr.push(`${j.num1}+${j.num2}`);
    } else if (tipo === 'MONAZOS') {
      // Solo número limpio, el subtipo se muestra como sub-encabezado
      arr.push(j.num1);
    } else {
      arr.push(j.num1);
    }
    montoMap.set(j.monto, arr);
  }

  const maxPerRow = 4;

  // Calcular ancho máximo del monto para alinear todas las líneas
  let maxMontoLen = 0;
  for (const [monto] of montoMap) {
    const ms = `\u20A1${monto.toLocaleString()}`;
    if (ms.length > maxMontoLen) maxMontoLen = ms.length;
  }

  const blocks: string[] = [];
  for (const [monto, nums] of montoMap) {
    const montoRaw = `\u20A1${monto.toLocaleString()}`;
    // Right-align monto al ancho máximo
    const montoStr = '\u00A0'.repeat(maxMontoLen - montoRaw.length) + montoRaw;
    const prefix = `${montoStr} | `;
    const padStr = '\u00A0'.repeat(prefix.length);

    // Dividir números en filas de máximo 4
    const rows: string[][] = [];
    for (let i = 0; i < nums.length; i += maxPerRow) {
      rows.push(nums.slice(i, i + maxPerRow));
    }

    // Primera línea: monto | nums   Continuación: indentada
    const lines: string[] = [];
    for (let i = 0; i < rows.length; i++) {
      const numsStr = rows[i].join(esReimpresion ? '*' : ' ');
      if (i === 0) {
        lines.push(`${escHtml(prefix)}${escHtml(numsStr)}`);
      } else {
        lines.push(`${padStr}${escHtml(numsStr)}`);
      }
    }

    blocks.push(`<div class="jugada-row">${lines.join('\n')}</div>`);
  }

  return blocks.join('\n');
}

/** Escapa HTML básico */
function escHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

// ─── Inline render (para preview dentro de React) ────────

/**
 * Genera el HTML del ticket body (sin <html>/<head>) para usar dentro de un iframe srcdoc.
 * Usa barcode inline vía canvas.
 */
export function renderTicketPreviewHTML(payload: TicketPayload): string {
  // Usamos el HTML completo para el iframe
  return renderTicketHTML(payload);
}

// ─── Impresión ───────────────────────────────────────────

/**
 * Abre una ventana de impresión con el ticket renderizado.
 * Retorna true si la ventana se abrió correctamente.
 */
export function imprimirTicket(payload: TicketPayload): boolean {
  const html = renderTicketHTML(payload);

  const printWindow = window.open('', '_blank', `width=${payload.config.ancho === 58 ? 420 : 560},height=700`);
  if (!printWindow) {
    console.error('[ticketService] No se pudo abrir ventana de impresión');
    return false;
  }

  printWindow.document.open();
  printWindow.document.write(html);
  printWindow.document.close();

  // Esperar a que cargue el barcode y luego imprimir
  printWindow.onload = () => {
    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
    }, 400);
  };

  return true;
}

// ─── Post-venta: flujo único ─────────────────────────────

export interface PostVentaParams {
  ticketCode: string;
  ticketId: string;
  sorteoNombre: string;
  sorteoCierre: string;
  sorteoFecha?: string;
  metodoPago: string;
  referencia?: string | null;
  total: number;
  jugadas: TicketJugada[];
  vendedorNombre?: string;
  localNombre?: string;
  pagaVecesAplicado?: string | null;
  clienteNombre?: string;
}

/**
 * procesarPostVenta: Punto de entrada único post-venta.
 * Genera el payload y lo retorna para que el UI decida mostrar preview.
 * 
 * NO modifica lógica de venta. Solo genera el payload del ticket.
 */
export async function procesarPostVenta(params: PostVentaParams): Promise<TicketPayload> {
  const payload = await crearTicketPayload({
    ...params,
    esReimpresion: false,
  });

  return payload;
}

/**
 * procesarReimpresion: Genera payload para reimpresión con marca.
 */
export async function procesarReimpresion(params: PostVentaParams): Promise<TicketPayload> {
  const payload = await crearTicketPayload({
    ...params,
    esReimpresion: true,
  });

  return payload;
}

// ─── Helpers ─────────────────────────────────────────────

function wrapText(texto: string, ancho: number): string {
  const words = texto.split(' ');
  const lines: string[] = [];
  let current = '';
  for (const word of words) {
    if ((current + ' ' + word).trim().length > ancho) {
      if (current) lines.push(current);
      current = word;
    } else {
      current = current ? `${current} ${word}` : word;
    }
  }
  if (current) lines.push(current);
  return lines.join('\n');
}
